import { baseApi } from 'services'

import { graphqlApi } from '@/graphql'
import { Address, ApplicationProfile, PaginatedResults, Profile } from '@/types/unlock'

export const applicationProfileV2 = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    pullCredit: builder.mutation<
      void,
      { applicationId: string; applicantProfileId: string; coApplicantProfileId: string }
    >({
      query: (arg) => ({
        url: `v2/applications/${arg.applicationId}/application-profiles/${arg.applicantProfileId}/pull-credit/`,
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'Tradeline',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'ApplicationProfiles',
        'Escalations',
        'Override',
        'Documents',
        'Alert',
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          // When credit is frozen we get a 400 response
          // and we should still invalidate the tags
        } finally {
          dispatch(
            graphqlApi.util.invalidateTags([
              'gql:application',
              'gql:applicationCalcs',
              'gql:applicationTradelinesCalcs',
            ])
          )
        }
      },
    }),
    changeContactType: builder.mutation<
      void,
      { applicationUuid: string; applicantProfileUuid: string; new_type: string }
    >({
      query: (arg) => ({
        url: `v2/applications/${arg.applicationUuid}/application-profiles/${arg.applicantProfileUuid}/switch-profile-type/`,
        method: 'PUT',
        body: { new_profile_type: arg.new_type },
      }),
      invalidatesTags: ['ApplicationProfiles'],
    }),
    listApplicationProfiles: builder.query<PaginatedResults<ApplicationProfile[]>, string>({
      query: (uuid) => ({
        url: `v2/applications/${uuid}/application-profiles/`,
        method: 'GET',
      }),
      providesTags: ['ApplicationProfiles'],
    }),
    getApplicationProfiles: builder.query<
      PaginatedResults<ApplicationProfile[]>,
      { externalId: string }
    >({
      query: ({ externalId }) => `v2/applications/${externalId}/application-profiles/`,
      providesTags: ['ApplicationProfiles'],
    }),
    listNonApplicants: builder.query<PaginatedResults<ApplicationProfile[]>, string>({
      query: (applicationUuid) => `v2/applications/${applicationUuid}/non-applicants/`,
      providesTags: ['ApplicationProfiles'],
    }),
    createNonApplicant: builder.mutation<
      ApplicationProfile,
      {
        applicationUuid: string
        address: Partial<Address>
        profile: Partial<Profile>
        otherFields?: Partial<ApplicationProfile>
      }
    >({
      query: ({ applicationUuid, ...payload }) => ({
        url: `v2/applications/${applicationUuid}/non-applicants/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ApplicationProfiles'],
    }),
    updateNonApplicant: builder.mutation<
      ApplicationProfile,
      {
        applicationUuid: string
        nonApplicantUuid: string
        otherFields?: Partial<ApplicationProfile>
      }
    >({
      query: ({ applicationUuid, nonApplicantUuid, ...payload }) => ({
        url: `v2/applications/${applicationUuid}/non-applicants/${nonApplicantUuid}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['ApplicationProfiles'],
    }),
    deleteNonApplicant: builder.mutation<
      void,
      { applicationUuid: string; nonApplicantUuid: string }
    >({
      query: ({ applicationUuid, nonApplicantUuid }) => ({
        url: `v2/applications/${applicationUuid}/non-applicants/${nonApplicantUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ApplicationProfiles'],
    }),
    switchProfileType: builder.mutation<
      void,
      { applicationUuid: string; applicantProfileUuid: string; new_type: string }
    >({
      query: (arg) => ({
        url: `v2/applications/${arg.applicationUuid}/non-applicants/${arg.applicantProfileUuid}/switch-profile-type/`,
        method: 'PUT',
        body: { new_profile_type: arg.new_type },
      }),
      invalidatesTags: ['ApplicationProfiles'],
    }),
  }),
})

export const {
  usePullCreditMutation,
  useListApplicationProfilesQuery,
  useLazyListApplicationProfilesQuery,
  useGetApplicationProfilesQuery,
  useLazyGetApplicationProfilesQuery,
  useChangeContactTypeMutation,
  useListNonApplicantsQuery,
  useCreateNonApplicantMutation,
  useUpdateNonApplicantMutation,
  useDeleteNonApplicantMutation,
  useSwitchProfileTypeMutation,
} = applicationProfileV2
